import styled from "@emotion/styled";
import {
    colors,
    colorVariables,
    fontSizes,
    gutterSizes,
    boxShadow,
    mediaQuery,
    breakpoints,
    fontWeights,
} from "@gumtree/ui-library/src/base/theme";
import { css } from "@emotion/react";
import { TextArea } from "@gumtree/ui-library";
import Input from "@gumtree/ui-library/src/form/input";
import type { FormType } from "./message";

interface MessageFormProps {
    formType: FormType;
    canOffer: boolean;
    hasError: boolean;
}

const shouldForwardProp = (p: string) => !["error"].includes(p);

export const MessageForm = styled.form<MessageFormProps>`
    display: grid;
    grid-template-areas:
        "provide-message"
        "provide-phone"
        ${({ hasError }) => (hasError ? '"error-messages"' : undefined)}
        "send-message";
    grid-row-gap: ${gutterSizes.base};

    fieldset[name="provide-message"],
    fieldset[name="provide-phone"] {
        border: 0;
        margin: 0;
        padding: 0;

        .input-clear {
            display: none;
        }
    }

    label {
        font-size: ${fontSizes.small};
        font-weight: ${fontWeights.bolder};
    }

    fieldset[name="provide-message"] {
        grid-area: provide-message;
        margin-bottom: ${({ formType }) =>
            formType === "desktop" ? undefined : gutterSizes.small};
    }

    fieldset[name="provide-phone"] {
        grid-area: provide-phone;

        p {
            margin-top: ${gutterSizes.medium};
        }

        &.error {
            input {
                border-color: ${colors.red};
                &:focus, &:focus-visible {
                    border-color: ${colors.red};
                    outline: none;
                }
            }
        }
    }

    ${({ formType, hasError }) =>
        (formType === "mobile" &&
            css`
                grid-template-columns: 3fr 1fr;
                grid-template-areas:
                    "provide-message send-message"
                    ${hasError ? '"error-messages error-messages"' : undefined};

                fieldset[name="provide-message"] {
                    margin-right: ${gutterSizes.base};
                }
            `) ||
        css`
            textarea {
                height: 150px;
                padding: 10px 13px;
            }
        `}

    input[name="form-message"],
    input[name="form-phone"] {
        width: 100%;
        box-sizing: border-box;
        min-width: 200px;
        text-overflow: ellipsis;
        white-space: nowrap;
        height: 44px;
        padding: 5px 8px;
        border: 1px solid #d8d6d9;
        border-radius: 2px;
        font-family: inherit;
        font-size: ${fontSizes.small};
        
        ${mediaQuery.until(breakpoints.medium)} {
            font-size: ${fontSizes.base};
        }
    }

    ${({ canOffer }) =>
        canOffer &&
        css`
            margin-bottom: ${gutterSizes.medium};
        `}
`;

export const MessageInput = styled(Input)`
    color: ${colors.bark};
    white-space: pre-line;
`;

export const MessageTextArea = styled(TextArea, {
    shouldForwardProp,
})<{ error: boolean }>`
    color: ${colors.bark};
    white-space: pre-line;
    border-color: ${({ error }) => (error ? colors.red : undefined)};
`;

export const ProSellerSubtitle = styled.p`
    font-size: ${fontSizes.small};
    color: ${colorVariables.fontTeritary};
    margin: 0;
`;

export const MotorsFeedSubtitle = styled.div`
    margin: ${gutterSizes.base} 0 0;
    font-size: ${fontSizes.small};
    color: ${colorVariables.fontTeritary};

    .hint-tooltip {
        display: inline-block;
        color: ${colorVariables.fontPrimary};
        font-size: ${fontSizes.small14};
        font-weight: ${fontWeights.normal};

        .icon {
            transform: translateY(-2px);
        }

        .hint-tooltip-content {
            transform: translateX(calc(-50% - 35px));
        }

        .hint-tooltip-content:before {
            left: calc(50% + 35px);
        }

        a {
            color: ${colorVariables.fontPrimary};
            font-size: ${fontSizes.small14};
            font-weight: ${fontWeights.normal};
            text-decoration: underline;
        }
    }
`;

export const Errors = styled.ul`
    grid-area: error-messages;

    li {
        color: ${colors.red};
    }
`;

export const PhoneErrors = styled.div`
    margin-top: ${gutterSizes.base};
    color: ${colors.red};
`;

export const Spinner = styled.div<{ spinning: boolean }>`
    grid-area: send-message;

    ${({ spinning }) =>
        spinning &&
        css`
            background: white;
            opacity: 0.5;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
        `}

    position: relative;

    .loader-spinner {
        display: ${({ spinning }) => (spinning ? "inline-block" : "none")};
        overflow: hidden;
        position: absolute;
        top: 50%;
        left: 50%;
        animation: spinnerRotate 0.8s linear infinite;

        border-radius: 30px;
        opacity: 1;
        margin-top: -20px;
        margin-left: -20px;

        &:before {
            content: "";
            display: block;
            width: 30px;
            height: 30px;
            margin: 5px;
            border-radius: 30px;
            box-shadow: ${boxShadow.spinnerLoader};
        }
    }

    @keyframes spinnerRotate {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
`;
